"use client";

import { curveLinear } from "@visx/curve";
import { LinePath } from "@visx/shape";
import { motion } from "framer-motion";
import React from "react";
import { useChartContext } from "../../../context/ChartContext";
import { BaseDataPoint } from "../../../utils/charts";
import { splitOnGaps } from "../../../utils/splitOnGaps";

interface LineSeriesProps {
  /**
   * The points to draw.
   *
   * Optional ONLY inside a streaming chart, where the points do not exist until
   * the reader looks at a window: there the child names its stream with
   * `seriesKey` and the chart supplies `data` before this component renders. In
   * an ordinary chart it is required in practice — a series with neither is
   * skipped rather than drawn.
   */
  data?: BaseDataPoint[];
  /**
   * Which stream this series draws, in a chart with a streaming `source`.
   *
   * Defaults to `"line"`, the primary stream. Ignored entirely by a chart that
   * holds its own data, so the same `<AreaSeries>` serves both sources.
   */
  seriesKey?: string;
  /**
   * Relabel this series once its points arrive, in a streaming chart.
   *
   * Declared here rather than only on the chart because the element carrying it
   * is this one; the streaming branch reads it, resolves it against every
   * assembled stream, and passes the result down as `label`. This component
   * never calls it — by the time it renders, `label` is already the answer.
   */
  labelFromData?: (
    plotted: BaseDataPoint[],
    series: import("../../../utils/timeSeriesTiles").AssembledSeries
  ) => string;
  color?: string;
  label?: string;
  /** Position in a tiled chart's legend, lowest first; series without one follow in draw order. */
  legendOrder?: number;
  /** Streaming primary plot only: an optional stream, dropped where the window has none of it, named where it fills only part. */
  partialCoverageLabel?: string;
  strokeWidth?: number;
  /** CSS stroke-dasharray value (e.g. "4 2" for dotted, "8 4" for dashed) */
  strokeDasharray?: string;
  categoryColors?: Record<string, string>;
  categoryLabels?: Record<string, string>;
  /**
   * When set, break the line wherever consecutive points are spaced more than
   * this many ms apart (missing buckets / outages). Omit to connect across gaps.
   */
  maxGapMs?: number;
  animate?: boolean;
}

/**
 * LineSeries
 *
 * Chart component for rendering line charts.
 * Displays data as a continuous line with customizable stroke width, color, and dash patterns.
 * When categoryColors are provided, contiguous line segments are colored by each segment
 * start point category while preserving tooltip and legend metadata from ChartContainer.
 */
export const LineSeries: React.FC<LineSeriesProps> = ({
  data: rawData,
  color = "var(--color-viz-default)",
  strokeWidth = 3,
  strokeDasharray,
  categoryColors = {},
  maxGapMs,
  animate = true,
}) => {
  const { xScale, yScale, animationSettings, entrance } = useChartContext();
  // Only the chart's first commit animates; a legend toggle re-mounts the series
  // and must not replay the grow-in.
  // Latched at mount, not recomputed per render: the flag flips in a passive effect,
  // and the chart re-renders while the entrance is still running — tooltip state
  // lives in this same context, so a hover does it. Recomputing would swap the
  // wrapper out mid-animation and snap the geometry to full scale.
  const [firstCommit] = React.useState(() => entrance?.played !== true);
  const playEntrance = animate && firstCommit;
  const yRange = yScale.range();
  const hasCategoryColors = Object.keys(categoryColors).length > 0;
  const getPointColor = (point: BaseDataPoint) => (point.category ? (categoryColors[point.category] ?? color) : color);
  const getDrawYValue = (point: BaseDataPoint) => point.chartYValue ?? point.yValue;

  // Absent only for a streaming child a `ChartContainer` has not filled yet, or
  // a stream this source does not produce. Draw nothing rather than throwing.
  const data = rawData ?? [];
  const singlePoint = data.length === 1 ? data[0] : undefined;
  const points = maxGapMs != null ? splitOnGaps(data, maxGapMs) : data;

  // Camera mode (animate=false) rides an ancestor's per-frame scaleX pan/zoom
  // transform; non-scaling-stroke holds the line at a constant width instead of
  // stretching with the geometry. Left off while the scaleY entrance animates.
  const strokeVectorEffect = animate ? undefined : "non-scaling-stroke";

  const children = singlePoint ? (
    <circle
      cx={xScale(singlePoint.xValue)}
      cy={yScale(getDrawYValue(singlePoint))}
      r={strokeWidth}
      fill={hasCategoryColors ? getPointColor(singlePoint) : color}
    />
  ) : hasCategoryColors ? (
    data.slice(0, -1).map((point, index) => {
      const nextPoint = data[index + 1];
      // Skip the connecting segment across a gap (missing buckets / outage).
      // Mirror splitOnGaps: a non-positive maxGapMs is a no-op.
      if (maxGapMs != null && maxGapMs > 0 && nextPoint.xValue.getTime() - point.xValue.getTime() > maxGapMs) {
        return null;
      }

      return (
        <LinePath
          key={`${point.xValue.getTime()}-${nextPoint.xValue.getTime()}`}
          data={[point, nextPoint]}
          x={(d) => xScale(d.xValue)}
          y={(d) => yScale(getDrawYValue(d))}
          stroke={getPointColor(point)}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          strokeLinecap="round"
          vectorEffect={strokeVectorEffect}
          curve={curveLinear}
        />
      );
    })
  ) : (
    <LinePath
      data={points}
      x={(d) => (d ? xScale(d.xValue) : 0)}
      y={(d) => (d ? yScale(getDrawYValue(d)) : 0)}
      defined={(d) => d != null}
      stroke={color}
      strokeWidth={strokeWidth}
      strokeDasharray={strokeDasharray}
      strokeLinecap="round"
      vectorEffect={strokeVectorEffect}
      curve={curveLinear}
    />
  );

  const gStyle = {
    transformOrigin: `0 ${yRange[0]}px`,
    transformBox: "fill-box" as const,
    isolation: "isolate" as const,
  };

  if (!playEntrance) {
    return <g style={gStyle}>{children}</g>;
  }

  return (
    <motion.g
      initial={{ scaleY: 0 }}
      animate={{ scaleY: 1 }}
      transition={{
        duration: animationSettings.duration,
        ease: animationSettings.ease as "easeInOut",
      }}
      style={gStyle}
    >
      {children}
    </motion.g>
  );
};
